/**
 * Plan settings page — current plan + tier cards with the upgrade CTA.
 * The Upgrade button is where Razorpay checkout will hook in (Phase 2); it's a
 * stub notice for now. Current plan comes from /api/editor/me.
 */
import { Fragment, useState } from "react";
import { Check, Minus } from "lucide-react";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useMe } from "@/hooks/use-auth";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay-checkout";
import { cn } from "@/lib/utils";
import { PLAN_CARDS, PLAN_ORDER, COMPARE_GROUPS, type PlanKey, type CompareCell } from "@/data/plans";

function CompareValue({ value }: { value: CompareCell }) {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-brand" aria-label="Included" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-muted-foreground/50" aria-label="Not included" />;
  return <span className="text-[13px] text-foreground">{value}</span>;
}

export function PlanSettingsPage() {
  const { data: me } = useMe();
  const current = (me?.plan as PlanKey | undefined) ?? undefined;
  const currentIdx = current ? PLAN_ORDER.indexOf(current) : -1;
  const isCustom = !!current && !PLAN_ORDER.includes(current); // e.g. FOUNDING
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [annual, setAnnual] = useState(false);
  const [remandatePrompt, setRemandatePrompt] = useState<{ planKey: PlanKey; name: string; direction: "upgrade" | "downgrade"; method: "card" | "upi" } | null>(null);
  const [annualSwitchPrompt, setAnnualSwitchPrompt] = useState<{ planKey: PlanKey; name: string; price: string } | null>(null);
  const currentCycle = (me?.planCycle as string | undefined) ?? undefined;

  // A plan change's grant usually lands via an async Razorpay webhook, so /me
  // won't show the new plan the instant checkout's handler fires. Poll /me a few
  // times until the plan (or billing cycle) actually changes, then clear the
  // transient notice. This replaces a single fixed 3s refetch that RACED the
  // webhook — if the webhook landed after 3s the UI stayed stale until a manual
  // refresh (fixed 2026-09-10).
  const ME_QUERY_KEY = ["auth", "workspace-me"] as const;
  function clearNoticeSoon() {
    setTimeout(() => setNotice(null), 9000);
  }
  async function pollForPlanChange() {
    const before = qc.getQueryData<{ plan?: string; planCycle?: string }>(ME_QUERY_KEY);
    const prevPlan = before?.plan;
    const prevCycle = before?.planCycle;
    // ~24s ceiling (12 × 2s) — generous headroom over normal webhook latency;
    // breaks the moment the change shows, so the happy path resolves in ~2s.
    for (let i = 0; i < 12; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      await qc.refetchQueries({ queryKey: ME_QUERY_KEY });
      const after = qc.getQueryData<{ plan?: string; planCycle?: string }>(ME_QUERY_KEY);
      if (after && (after.plan !== prevPlan || after.planCycle !== prevCycle)) break;
    }
    setNotice(null);
  }

  async function startCheckout(planKey: PlanKey, name: string, cycle: "monthly" | "annual", remandate = false) {
    setNotice(null);
    setBusy(planKey);
    try {
      const res = await api.post<{
        updated?: boolean;
        scheduleChangeAt?: string;
        subscriptionId?: string;
        orderId?: string;
        amount?: number;
        keyId?: string;
        remandate?: boolean;
        direction?: string;
        method?: string;
      }>("/billing/subscribe", { planKey, cycle, remandate });

      // Mandate can't be updated in place (UPI either way, or a CARD upgrade) → confirm
      // replacing it before doing anything. method drives the dialog copy.
      if (res.remandate) {
        setRemandatePrompt({
          planKey,
          name,
          direction: res.direction === "downgrade" ? "downgrade" : "upgrade",
          method: res.method === "card" ? "card" : "upi",
        });
        return;
      }

      // Existing subscription changed in place (no second checkout / no double charge).
      if (res.updated) {
        setNotice(
          res.scheduleChangeAt === "cycle_end"
            ? `Your plan will switch to ${name} at the end of your current billing cycle.`
            : `Upgrading to ${name} now — this takes a few seconds to reflect.`
        );
        // cycle_end = scheduled downgrade, nothing changes NOW → just clear the
        // notice. An immediate in-place change → poll until /me reflects it.
        if (res.scheduleChangeAt === "cycle_end") clearNoticeSoon();
        else void pollForPlanChange();
        return;
      }
      await loadRazorpay();
      const Rzp = (window as unknown as { Razorpay: new (o: unknown) => { open: () => void; on: (e: string, cb: () => void) => void } }).Razorpay;

      // ANNUAL → one-time Razorpay Order (no autopay). Plan activates via webhook.
      if (cycle === "annual") {
        if (!res.orderId || !res.keyId) {
          setNotice("Could not start checkout. Please try again.");
          return;
        }
        const rzpOrder = new Rzp({
          key: res.keyId,
          order_id: res.orderId,
          amount: res.amount,
          currency: "INR",
          name: "Editlock",
          description: `${name} plan — 1 year`,
          theme: { color: "#7b2ff7" },
          handler: () => {
            setNotice(`Payment received — your annual ${name} plan is activating. If you switched from another paid plan, the unused portion of it is refunded to your original payment method within ~5–7 business days.`);
            void pollForPlanChange();
          },
          modal: { ondismiss: () => setBusy(null) },
        });
        rzpOrder.on("payment.failed", () => setNotice("Payment failed. Please try again."));
        rzpOrder.open();
        return;
      }

      // MONTHLY → Razorpay Subscription.
      if (!res.subscriptionId || !res.keyId) {
        setNotice("Could not start checkout. Please try again.");
        return;
      }
      const rzp = new Rzp({
        key: res.keyId,
        subscription_id: res.subscriptionId,
        name: "Editlock",
        description: `${name} plan`,
        theme: { color: "#7b2ff7" },
        handler: () => {
          setNotice(`Payment received — your ${name} plan is activating. This can take a few seconds.`);
          void pollForPlanChange();
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", () => setNotice("Payment failed. Please try again."));
      rzp.open();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not start checkout.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <SettingsPageHeader title="Plan" subtitle="Your plan and what each tier includes." />

      {remandatePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-lg">
            <h3 className="font-display text-lg font-bold tracking-tight">
              {remandatePrompt.direction === "downgrade" ? `Switch to ${remandatePrompt.name}` : `Upgrade to ${remandatePrompt.name}`}
            </h3>
            <p className="mt-3 text-sm text-muted-foreground">
              {remandatePrompt.method === "card"
                ? `Card payments can't be upgraded in place. To upgrade to ${remandatePrompt.name}, we'll set up a new payment for ${remandatePrompt.name} — charged now — then refund the unused days of your current plan to your original card (within ~5–7 business days). You'll confirm the payment once more.`
                : remandatePrompt.direction === "downgrade"
                ? `Your current plan is paid by UPI, which can't be changed in place. To switch to ${remandatePrompt.name}, you'll authorize a new, lower UPI mandate now — but it won't be charged until your current billing cycle ends. Your current plan stays active until then, and there's no refund for the remaining days.`
                : `Your current plan is paid by UPI, which can't be changed in place. To upgrade to ${remandatePrompt.name}, we'll cancel your current UPI mandate and refund the unused days of this cycle to your original payment method (within ~5–7 business days), then set up a new mandate for ${remandatePrompt.name}, charged now.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRemandatePrompt(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { planKey, name } = remandatePrompt;
                  setRemandatePrompt(null);
                  startCheckout(planKey, name, "monthly", true);
                }}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:opacity-90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {annualSwitchPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-lg">
            <h3 className="font-display text-lg font-bold tracking-tight">
              Switch to {annualSwitchPrompt.name} (annual)
            </h3>
            <p className="mt-3 text-sm text-muted-foreground">
              You'll be charged the full {annualSwitchPrompt.price} for {annualSwitchPrompt.name} now. The unused portion of your current plan is then refunded to your original payment method within ~5–7 business days — it isn't deducted from this charge.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setAnnualSwitchPrompt(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { planKey, name } = annualSwitchPrompt;
                  setAnnualSwitchPrompt(null);
                  startCheckout(planKey, name, "annual");
                }}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:opacity-90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {isCustom && (
        <div className="mb-6 rounded-2xl border border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
          You're on a custom plan (
          <span className="font-medium text-foreground">{me?.limits?.label}</span>). Contact us to make changes.
        </div>
      )}

      {notice && (
        <div className="mb-6 rounded-xl border border-brand/40 bg-brand-soft/40 px-4 py-3 text-sm text-foreground">
          {notice}
        </div>
      )}

      <div className="mb-6 flex items-center justify-center gap-3 text-sm sm:justify-end">
        <span className={cn("font-medium", annual ? "text-muted-foreground" : "text-foreground")}>Monthly</span>
        <button
          type="button"
          role="switch"
          aria-checked={annual}
          aria-label="Toggle annual billing"
          onClick={() => setAnnual((v) => !v)}
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
            annual ? "bg-brand" : "bg-muted"
          )}
        >
          <span
            className={cn(
              "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
              annual ? "translate-x-[22px]" : "translate-x-0.5"
            )}
          />
        </button>
        <span className={cn("font-medium", annual ? "text-foreground" : "text-muted-foreground")}>
          Annually <span className="text-brand">(10% off)</span>
        </span>
      </div>

      <div className="flex gap-5 overflow-x-auto px-1 pt-3 pb-4">
        {PLAN_CARDS.map((p) => {
          const idx = PLAN_ORDER.indexOf(p.key);
          const sameTier = current === p.key;
          // Treat null/unknown cycle as monthly — pre-migration paid rows have plan_cycle=NULL
          // and monthly subs only get it set on their next webhook, so "monthly" must match null too.
          const cycleMatches = p.key === "FREE" ? true : (annual ? currentCycle === "annual" : currentCycle !== "annual");
          const isCurrent = sameTier && cycleMatches;
          const isDowngrade = currentIdx > -1 && idx < currentIdx;
          return (
            <div
              key={p.key}
              className={cn(
                "relative flex w-[320px] shrink-0 flex-col rounded-2xl border border-border bg-card/60 p-5",
                isCurrent && "border-brand/60 shadow-card ring-1 ring-brand"
              )}
            >
              {isCurrent && (
                <span className="absolute -top-2.5 left-5 rounded-full bg-brand px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-brand-foreground">
                  Current
                </span>
              )}
              <div className="font-mono text-xs uppercase tracking-widest text-brand">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-2xl font-black tracking-tight">
                  {annual ? p.annualPriceLabel : p.priceLabel}
                </span>
                <span className="text-sm text-muted-foreground">
                  {annual ? (p.key === "FREE" ? "forever" : "/yr") : p.period}
                </span>
              </div>
              {annual && p.key !== "FREE" && (
                <div className="mt-1 text-xs font-medium text-brand">Save 10% vs monthly</div>
              )}
              <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
              <div className="mt-3 space-y-1 text-sm">
                <div>{p.storage}</div>
                <div className="text-muted-foreground">{p.members}</div>
              </div>
              <button
                type="button"
                disabled={isCurrent || busy === p.key}
                onClick={() => {
                  // Annual switch FROM an active paid plan = full charge now + async
                  // refund of the old plan's unused remainder → confirm first so the full
                  // charge isn't a surprise. Fresh Free→paid has no refund, so skip the prompt.
                  if (annual && current && current !== "FREE" && (current as string) !== "TRIAL" && !isCurrent) {
                    setAnnualSwitchPrompt({ planKey: p.key, name: p.name, price: p.annualPriceLabel });
                  } else {
                    startCheckout(p.key, p.name, annual ? "annual" : "monthly");
                  }
                }}
                className={cn(
                  "mt-4 inline-flex w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                  isCurrent
                    ? "cursor-default border border-border text-muted-foreground"
                    : p.featured
                      ? "bg-brand text-brand-foreground hover:opacity-90"
                      : "border border-border hover:bg-muted"
                )}
              >
                {isCurrent
                  ? "Current plan"
                  : busy === p.key
                    ? "Starting…"
                    : annual && sameTier
                      ? "Switch to annual"
                      : !annual && sameTier && currentCycle === "annual"
                        ? "Switch to monthly"
                        : isDowngrade
                        ? "Switch to this"
                        : "Upgrade"}
              </button>
              <ul className="mt-5 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-[13px] text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-12 border-t border-border pt-10">
        <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Compare plans</h2>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="sticky left-0 z-10 bg-card px-4 py-3 text-left font-medium text-muted-foreground">Feature</th>
                {PLAN_CARDS.map((p) => (
                  <th
                    key={p.key}
                    className={cn(
                      "px-3 py-3 text-center align-bottom",
                      current === p.key && "text-brand"
                    )}
                  >
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs font-normal text-muted-foreground">
                      {annual ? p.annualPriceLabel : p.priceLabel}
                      {annual ? (p.key === "FREE" ? "" : "/yr") : (p.period === "/mo" ? "/mo" : "")}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_GROUPS.map((g) => (
                <Fragment key={g.group}>
                  <tr>
                    <td
                      colSpan={PLAN_CARDS.length + 1}
                      className="bg-secondary/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                    >
                      {g.group}
                    </td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.label} className="border-t border-border">
                      <td className="sticky left-0 z-10 bg-card px-4 py-2.5 text-left text-muted-foreground">
                        {r.label}
                      </td>
                      {r.cells.map((c, i) => (
                        <td
                          key={i}
                          className={cn(
                            "px-3 py-2.5 text-center",
                            current === PLAN_ORDER[i] && "bg-brand/5"
                          )}
                        >
                          <CompareValue value={c} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        Prices are all-inclusive INR. Reviewers and clients are unlimited and free on every plan.
        Storage is a hard cap — no surprise overage. Annual plans are billed once for the year (10% off)
        and don't auto-renew — we'll remind you before they expire.
      </p>
    </div>
  );
}
